import { useState } from 'react';
import { MARKET_PROMOTION_PERMISSIONS } from '@/constants/permissions';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { ExportButton } from '@/components/common/ExportButton';
import { StatCardGrid, type LabeledStat } from '@/components/common/StatCardGrid';
import { TAKA_SYMBOL } from '@/constants/app.constants';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { exportMpApprovals, mpApprovalsInfiniteQueryOptions } from '../api/marketPromotion.api';
import { DonationApprovalFilterPanel } from '../components/DonationApprovalFilterPanel';
import { APPROVAL_ZONAL_MANAGER_COLUMN_ID, getMpApprovalColumns } from '../components/MpColumns';
import {
  DONATION_DETAIL_ORIGIN,
  DONATION_SEARCH_PLACEHOLDER,
} from '../constants/marketPromotion.constants';
import { useMpApprovalFilters } from '../hooks/useMpApprovalFilters';
import { useOpenDonationDetail } from '../hooks/useOpenDonationDetail';
import type { MpApprovalResponse } from '../types/marketPromotion.types';

type ApprovalListMeta = MpApprovalResponse['meta'];

function getApprovalStatCards(meta: ApprovalListMeta | undefined): LabeledStat[] {
  return [
    { key: 'donations', label: 'Donations', value: meta?.total },
    { key: 'pending', label: 'Pending', value: meta?.totals.pending },
    { key: 'recommended', label: 'Recommended', value: meta?.totals.recommended },
    { key: 'approved', label: 'Approved', value: meta?.totals.approved },
    { key: 'rejected', label: 'Rejected', value: meta?.totals.rejected },
    {
      key: 'donationAmount',
      label: 'Donation Amount',
      value: meta === undefined ? undefined : Math.round(meta.totals.approvedAmount),
      unit: TAKA_SYMBOL,
    },
  ];
}

interface MpApprovalPageProps {
  routeId: 'approvals' | 'mis';
  title: string;
  showCall: boolean;
}

export function MpApprovalPage({ routeId, title, showCall }: MpApprovalPageProps) {
  const controller = useMpApprovalFilters(routeId);
  const { rows, firstPageMeta, isLoading, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(mpApprovalsInfiniteQueryOptions(controller.params, routeId === 'mis'));

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { openDonationDetail, decisionActions, isDonationHighlighted } = useOpenDonationDetail(
    DONATION_DETAIL_ORIGIN[routeId],
  );

  const columns = getMpApprovalColumns(
    {
      ...decisionActions,
      onView: openDonationDetail,
      ...(showCall ? { onVerify: openDonationDetail } : {}),
    },
    showCall,
    controller.params.search,
  );

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title={title}
        titleAddon={
          <DateRangeBadge
            onClick={() => setIsFilterOpen(true)}
            dateFrom={controller.params.dateFrom}
            dateTo={controller.params.dateTo}
          />
        }
        search={{
          value: controller.searchText,
          onChange: controller.setSearchText,
          placeholder: DONATION_SEARCH_PLACEHOLDER,
        }}
        actions={
          <>
            <AdvancedFilterButton
              isOpen={isFilterOpen}
              activeCount={controller.activeFilterCount}
              onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
            />
            <ExportButton
              requires={MARKET_PROMOTION_PERMISSIONS.APPROVAL_EXPORT}
              onExport={() => exportMpApprovals(controller.params, showCall)}
              disabled={isLoading || loadError !== null}
            />
          </>
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <DonationApprovalFilterPanel filters={controller} isMisQueue={showCall} />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the queue"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <>
          <StatCardGrid stats={getApprovalStatCards(firstPageMeta)} isLoading={isLoading} />
          <DataTable
            data={rows}
            columns={columns}
            isLoading={isLoading}
            className="lg:flex-1"
            stickyColumnId={APPROVAL_ZONAL_MANAGER_COLUMN_ID}
            isRowHighlighted={isDonationHighlighted}
            emptyMessage={
              showCall
                ? 'Nobody has called about a donation in this period'
                : 'No cash donation is waiting'
            }
            {...loadMoreProps}
          />
        </>
      )}
    </div>
  );
}
